import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';

import TuneRoundedIcon from '@material-ui/icons/TuneRounded';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import { Button, IconButton, InputBase } from '@material-ui/core';
import { useOutsideAlerter } from 'custom-hooks';
import SearchFilter, {
  SearchValues,
} from 'store/models/SearchFilter';
import { CloseRounded, RefreshRounded } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  countriesActions,
  workdeskFilterActions,
  workdeskTicketsActions,
} from 'store/actions';
import { workdeskFilterSearchValuesSelector } from 'store/selectors/workdesk-filter.selector';
import { assigneesDataSelector } from 'store/selectors/assignees.selector';
import { countriesDataSelector } from 'store/selectors/countries.selector';
import CustomDateRangePicker from '../common/CustomDateRangePicker';
import MultiTextField from './search-bar/MultiTextField';
import { assigneesActions } from 'store/actions';
import MultiAutocompleteWithSelector from './search-bar/MultiAutocompleteWithSelector';
import ClearableSingleTextField from './search-bar/ClearableSingleTextField';
import Booking from 'store/models/bookings/Booking';
import { isWsConnectedSelector } from 'store/selectors';

const SearchBar = () => {
  const [showAdvance, setShowAdvance] = useState(false);
  const [active, setActive] = useState(false);

  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);

  const wdSearchValues = useSelector(
    workdeskFilterSearchValuesSelector,
  );
  // advance search values befire hit search button
  const [
    tmpSearchValues,
    setTmpSearchValues,
  ] = useState<SearchValues>(
    SearchFilter.getInitialAdvanceFilterValue(),
  );

  const assignees = useSelector(assigneesDataSelector);
  const countries = useSelector(countriesDataSelector);
  const wsIsConnected = useSelector(isWsConnectedSelector);

  /**
   * fetch assignees onload
   */
  useEffect(() => {
    // check length to prevent loading when back from main page
    if (!assignees || !assignees.length) {
      stableDispatch(assigneesActions.loadAssignees());
    }
    if (!countries || !countries.length) {
      stableDispatch(countriesActions.loadCountries());
    }
  }, [assignees, countries, stableDispatch]);

  useEffect(() => {
    setTmpSearchValues(
      SearchFilter.storeStateToSearchValues(wdSearchValues),
    );

    // handleSubmitSearch();
  }, [wdSearchValues]);

  const hasSearchValues = useMemo(
    () => SearchFilter.hasAnySearchValues(tmpSearchValues),
    [tmpSearchValues],
  );

  const hasNonSubjectSearchValues = useMemo(
    () => SearchFilter.hasNonSubjectSearchValues(tmpSearchValues),
    [tmpSearchValues],
  );
  const showSearchButton = useMemo(
    () => hasSearchValues && !showAdvance,
    [hasSearchValues, showAdvance],
  );

  const handleCloseAdvance = () => {
    setShowAdvance(false);
  };
  const handleToggleAdvance = () => {
    setShowAdvance(!showAdvance);
  };
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  // doesn't work with the multi select
  useOutsideAlerter(panelRef, () => {
    if (!onEdit.current) handleCloseAdvance();
    // comment below back to get auto submit when click outside
    // if (_hasChangeRef.current) handleSubmitSearch();
  });
  const handleSetActive = (val: boolean) => {
    setActive(val);
  };

  const [mainTmpValue, setMainTmpValue] = useState('');
  const recentMainMatchedField = useRef<
    'subject' | 'bookingNos' | 'ticketIds'
  >('subject');

  const searchWithFieldLabel = SearchFilter.getFieldLabel(
    recentMainMatchedField.current,
  );

  /**
   *
   * @param value
   */
  const handleMainTmpValueChange = (value) => {
    setMainTmpValue(value);
    // check if match booking no regex
    const field = SearchFilter.searchValueIsField(value);
    if (field === 'subject') {
      handleValueChange(field, value);
    } else {
      let values = SearchFilter.splitString(value);
      if (field === 'bookingNos') {
        // auto replace ONEY
        values = values.map((v) => {
          if (Booking.isBookingNo(v)) {
            return Booking.replaceONEY(v);
          }
          return v;
        });
      }
      handleValueChange(field, values);
    }
    if (recentMainMatchedField.current !== field) {
      handleValueChange(
        recentMainMatchedField.current,
        recentMainMatchedField.current === 'subject' ? '' : [],
      );
    }
    recentMainMatchedField.current = field;
  };

  const handleValueChange = (name, value) => {
    if (name === 'bookingNos') {
      // auto replace ONEY
      if (Array.isArray(value) && value.length > 0) {
        value = value.map((v) => {
          if (Booking.isBookingNo(v)) {
            return Booking.replaceONEY(v);
          }
          return v;
        });
      }
    }
    setTmpSearchValues((all) => {
      return { ...all, [name]: value };
    });
  };
  const handleKeyUp = (e) => {
    if (e.key === 'Enter') {
      console.info(`trigger search`, tmpSearchValues);
      handleSubmitSearch();
    }
  };
  const advanceFilters = useMemo(() => {
    return SearchFilter.getAdvanceSearchFilters();
  }, []);
  const handleReset = () => {
    const val = SearchFilter.getInitialAdvanceFilterValue();
    setMainTmpValue('');
    setTmpSearchValues(val);
    // auto search after clear
    handleSubmitSearch(val);
  };

  const handleSubmitSearch = (val = tmpSearchValues) => {
    const pingWsAfter =
      wsIsConnected && !SearchFilter.hasAnySearchValues(val); // if clear
    const storeState = SearchFilter.tmptoStoreState(val);
    console.debug(
      `tmpSearchValues: `,
      val,
      'store',
      storeState,
      'pingWsAfter',
      pingWsAfter,
    );
    dispatch(workdeskFilterActions.setSearchValues(storeState));
    dispatch(workdeskTicketsActions.loadWorkdeskTickets(pingWsAfter));
    // auto close
    handleCloseAdvance();
  };
  const onEdit = useRef(false);
  const handleSetOnEdit = (value: boolean) => {
    onEdit.current = value;
  };
  const handleRefetch = () => {
    dispatch(workdeskTicketsActions.refetchWorkdeskTickets());
  };
  const renderSearchField = (item) => {
    if (item.type === 'TEXT') {
      return (
        <ClearableSingleTextField
          key={item.name}
          name={item.name}
          label={item.label}
          value={tmpSearchValues[item.name] || ''}
          disabled={item.disabled}
          handleValueChange={handleValueChange}
        />
      );
    } else if (item.type === 'MULTI_TEXT') {
      return (
        <MultiTextField
          key={item.name}
          label={item.label}
          value={tmpSearchValues[item.name]}
          onChange={(e) =>
            handleValueChange(item.name, e.target.value)
          }
          disabled={item.disabled}
        />
      );
    } else if (item.type === 'DATE_RANGE') {
      return (
        <CustomDateRangePicker
          key={item.name}
          label={item.label}
          value={tmpSearchValues[item.name]}
          onChange={({ startDate, endDate }) =>
            // startDate and endDate are moment object
            handleValueChange(item.name, { startDate, endDate })
          }
          disabled={item.disabled}
        />
      );
    } else if (item.type === 'MULTI_SELECT') {
      if (!item.dataSelector)
        throw Error(`MULTI_SELECT needs item.dataSelector for now`);
      return (
        <MultiAutocompleteWithSelector
          key={item.name}
          label={item.label}
          dataSelector={item.dataSelector}
          loadDataErrorSelector={item.loadDataErrorSelector}
          handleSetOnEdit={handleSetOnEdit}
          value={tmpSearchValues[item.name]}
          onChange={(event) => {
            handleValueChange(item.name, event.target.value);
          }}
          disabled={item.disabled}
        />
      );
    }
  };
  return (
    <SWrapper ref={panelRef}>
      <SSearchBarAndFloat>
        <SSearchBar
          className={`${active ? 'active' : ''} ${
            hasSearchValues ? 'has-value' : ''
          }`}
        >
          <SSearchOrLoad>
            <SSearchIcon />
          </SSearchOrLoad>
          {/* question: when to trigget search? right now is onEnter*/}
          <SSearchBarInput
            value={mainTmpValue || ''}
            ref={searchInputRef}
            onFocus={() => handleSetActive(true)}
            onBlur={() => {
              handleSetActive(false);
            }}
            onChange={(e) => handleMainTmpValueChange(e.target.value)}
            placeholder="Type Subject/Ticket ID/Booking No"
            onKeyUp={(e) => handleKeyUp(e)}
          />
          <SReset
            onClick={() => {
              handleReset();
            }}
          >
            <CloseRounded />
          </SReset>
          <SAdvanceToggler
            onClick={() => handleToggleAdvance()}
            color="primary"
            className={
              hasNonSubjectSearchValues ? 'has-adv-value' : ''
            }
          >
            <STuneRoundedIcon />
          </SAdvanceToggler>
        </SSearchBar>
        <STopButtonsWrapper>
          <STopButton
            className={showSearchButton ? '' : 'hide'}
            color="primary"
            variant="contained"
            startIcon={<SearchRoundedIcon />}
            onClick={() => handleSubmitSearch()}
            disabled={!showSearchButton}
          >
            Search
          </STopButton>
          <SUpdateDataButton
            color="primary"
            variant="contained"
            onClick={() => handleRefetch()}
            startIcon={<RefreshRounded />}
          >
            Refresh
          </SUpdateDataButton>
        </STopButtonsWrapper>
      </SSearchBarAndFloat>

      {!showAdvance && mainTmpValue && (
        <SFieldMatched>
          {`Searching with field:`}
          <SField>{searchWithFieldLabel}</SField>
        </SFieldMatched>
      )}

      <SAdvanceSearchPanel className={showAdvance ? '' : 'hide'}>
        <SHeaderLabel>Multiple Search</SHeaderLabel>
        <SAdvanceSearchFields>
          {advanceFilters.map((item) => renderSearchField(item))}
        </SAdvanceSearchFields>
        <SFooter>
          {([
            {
              label: 'Clear',
              variant: 'outlined',
              onClick: () => handleReset(),
            },
            {
              label: 'Search',
              variant: 'contained',
              onClick: () => handleSubmitSearch(),
            },
          ] as {
            label: string;
            variant?: 'text' | 'outlined' | 'contained';
            onClick: Function;
          }[]).map((item) => (
            <Button
              key={item.label}
              variant={item.variant}
              color="primary"
              onClick={(e) => item.onClick(e)}
            >
              {item.label}
            </Button>
          ))}
        </SFooter>
      </SAdvanceSearchPanel>
    </SWrapper>
  );
};

export default SearchBar;

const SWrapper = styled.div`
  position: relative;
`;

const SSearchIcon = styled(SearchRoundedIcon)`
  color: ${(props) => props.theme.palette.text.secondary};
  &.MuiSvgIcon-root {
    font-size: 1.8rem;
  }
`;
const SAdvanceSearchPanel = styled.div`
  z-index: 2;
  position: absolute;
  width: 100%;

  margin-top: 0.5rem;
  &.hide {
    display: none;
  }
  background: #ffffff;
  box-shadow: 0px 6px 16px -2px rgba(0, 0, 0, 0.25);
  border-radius: 10px 0px 10px 10px;
`;
const SHeaderLabel = styled.div`
  font-size: 18px;
  color: ${(props) => props.theme.palette.text.primary};
  padding: 1rem;
`;
const SAdvanceSearchFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 0.5rem;
  row-gap: 0.5rem;
  padding: 0 1rem;
  padding-bottom: 3rem;
`;

const SFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem 1rem;
  border-top: solid 1px
    ${(props) => props.theme.palette.common.borderGrey};
  & .MuiButton-root + .MuiButton-root {
    margin-left: 0.5rem;
  }
`;
const SSearchBarInput = styled(InputBase)`
  & > .MuiInputBase-input {
    outline: none;
    font-size: ${(props) => props.theme.typography.fontSize.regular};
    border: none;
    letter-spacing: 0.5px;
  }
  flex: 1;
`;
const SAdvanceToggler = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 7px;
  }
  &.has-adv-value {
    background: rgb(183 63 131 / 15%);
    &:hover {
      background: rgb(183 63 131 / 15%);
    }
  }
`;
const STuneRoundedIcon = styled(TuneRoundedIcon)`
  /* color: ${(props) => props.theme.palette.primary.main}; */
`;
const SSearchBar = styled.div`
  &.has-value:not(.active) {
    border-color: #c53185;
    outline: 0;
    box-shadow: 0 0 8px 0.2rem rgb(197 49 133 / 35%);
  }
  background: #ffffff;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  &.active {
    border-color: ${(props) => props.theme.palette.common.borderBlue};
    outline: 0;
    box-shadow: 0 0 3px 0.2rem rgba(0, 123, 255, 0.25);
  }
  transition: box-shadow 0.15s ease-in-out;
`;
const SReset = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 7px;
    margin-right: -5px;
  }
`;
const SSearchBarAndFloat = styled.div`
  position: relative;
`;
const STopButton = styled(Button)`
  &.MuiButton-root {
  }
  &.hide.MuiButton-root {
    display: none;
  }
`;
const SSearchOrLoad = styled.div`
  width: 2rem;
`;
const STopButtonsWrapper = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  top: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const SUpdateDataButton = styled(Button)`
  white-space: nowrap;
`;
const SFieldMatched = styled.div`
  position: absolute;
  top: -18px;
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: ${(props) => props.theme.typography.fontSize.extraSmall};
  left: 5px;
`;
const SField = styled.div`
  display: inline;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  margin-left: 5px;
`;
